import { useResizeObserver } from 'beautiful-react-hooks'
import cx from 'classnames'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import GeneralPromptStyles from 'common/Prose/Prompts/Prompt/Prompt.module.scss'
import { RefObject, useEffect, useRef, useState } from 'react'
import Masonry from 'react-masonry-css'
import { SelectedImage, SetState } from 'types'
import Styles from '../Prompt.module.scss'
import { adjustPreviewMaxHeight } from '../utils'
import { ImageWithProgressPercentage } from './ImageWithProgressPercentage'
import InvalidPhoto from './InvalidPhoto'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Check2, ImageAdd } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Carousel } from 'components/Carousel/Carousel'

export interface Props {
  stagedImages: SelectedImage[]
  selectedImages: SelectedImage[]
  setSelectedImages: SetState<SelectedImage[]>
  loadMore?: () => void
  hasMore?: boolean
  multiSelect?: boolean
  uploaderInputRef?: RefObject<HTMLInputElement>
  showAddImage?: boolean
  useGrid?: boolean
  useCarousel?: boolean
}

const Preview = ({
  stagedImages,
  selectedImages,
  setSelectedImages,
  loadMore,
  hasMore,
  multiSelect = true,
  uploaderInputRef,
  showAddImage = false,
  useGrid = true,
  useCarousel = false,
}: Props) => {
  const [hasError, setHasError] = useState(false)
  const { isMobile } = useResponsiveDesign()
  useEffect(() => {
    setHasError(false)
  }, [stagedImages])

  const singleImage = stagedImages.length === 1

  const wrapperRef = useRef<HTMLInputElement>(null)
  const rect = useResizeObserver(wrapperRef)

  useEffect(() => {
    const wrapper = document.querySelector<HTMLElement>(`.${GeneralPromptStyles.promptWrapper}`)

    if (wrapper) {
      const top = parseFloat(window.getComputedStyle(wrapper).getPropertyValue('top'))

      adjustPreviewMaxHeight(wrapper, { top: top })
    }
  }, [rect])

  const selectImage = (image: SelectedImage) => {
    if (!multiSelect) {
      setSelectedImages([image])
      return
    }

    if (selectedImages.some((img) => img.url === image.url)) {
      setSelectedImages(selectedImages.filter((img) => img.url !== image.url))
    } else {
      setSelectedImages((images: SelectedImage[]) => [...images, image])
    }
  }

  const renderImages = () =>
    stagedImages.map((image) => (
      <div key={image.url} className={Styles.imageContainer}>
        <ImageWithProgressPercentage
          alt="preview"
          src={image.url}
          useOriginalSrc
          className={cx({
            [Styles.previewImageSelected]: !isMobile && selectedImages.some((img) => img.url === image.url),
          })}
          onClick={() => selectImage(image)}
          onError={() => setHasError(true)}
          overridingStyle={singleImage && !isMobile ? { width: '370px' } : undefined}
        />
        {isMobile && selectedImages.some((img) => img.url === image.url) && (
          <div className={Styles.imageSelectedCheck}>
            <Check2 />
          </div>
        )}
        {image.unsplash && image.unsplash.authorName && !isMobile && (
          <p className={Styles.authorWrapper}>{image.unsplash.authorName}</p>
        )}
      </div>
    ))

  const addImage = () => (
    <div className={Styles.addMedia} onClick={() => uploaderInputRef?.current?.click()}>
      <ImageAdd />
      <Text variant="smallList" color="var(--dark-80)">
        Add Media
      </Text>
    </div>
  )

  if (useCarousel)
    return (
      <div ref={wrapperRef} className={Styles.previewContainerCarousel}>
        <Carousel>{renderImages()}</Carousel>
      </div>
    )

  return (
    <div ref={wrapperRef} className={cx(Styles.previewContainer, { [Styles.singleImage]: singleImage && !useGrid })}>
      {hasError && <InvalidPhoto />}
      {!hasError && singleImage && (
        <>
          {isMobile ? (
            <>{renderImages()}</>
          ) : (
            <div id="searchResults" className={Styles.singleImageContainer}>
              {renderImages()}
            </div>
          )}
          {showAddImage && addImage()}
        </>
      )}
      {!hasError && !singleImage && (
        <>
          {isMobile ? (
            <>
              {renderImages()}
              {showAddImage && addImage()}
              <div className={Styles.scroll}>
                {loadMore && hasMore && <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />}
              </div>
            </>
          ) : (
            <div id="searchResults">
              <Masonry breakpointCols={2} className={Styles.masonryGrid} columnClassName={Styles.masonryGridColumn}>
                {renderImages()}
              </Masonry>
              <div className={Styles.scroll}>
                {loadMore && hasMore && <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Preview
